import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { Album } from '../../../models/album.model';
import { DialogService } from '../../components/dialogs/dialog.service';
import { Group } from '../../../models/group.model';
import { SchoolMemberType } from '../../../models/member.school.model';
import { MembershipStatus } from '../../../models/membership.status';
import { SchoolRoles } from '../../../models/roles.school.model';
import { UserModel } from '../../../models/user.model';
import { MomentPipe } from '../../../pipes/moment.pipe';
import { AlbumService } from '../../../services/album.service';
import { AuthService } from '../../../services/auth.service';
import { SchoolRolesService } from '../../../services/school-roles.service';
import { UtilsService } from '../../../services/utils.service';
import * as albumActions from '../../../state/actions/album.actions';
import { AppState } from '../../../state/appstate';
import * as albumSelector from '../../../state/selectors/album.selector';
import * as groupSelector from '../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import {
  BtnFabComponent,
  BtnFabPosition,
} from '../../components/buttons/btn-fab/btn-fab.component';
import { BtnMoreComponent } from '../../components/buttons/btn-more/btn-more.component';
import { LoadingCardComponent } from '../../components/loaders/loading-card/loading-card.component';
import { DlgAddImagesComponent } from '../dlg-add-images/dlg-add-images.component';
import { DlgCreateAlbumComponent } from '../dlg-create-album/dlg-create-album.component';
import { DlgEditAlbumComponent } from '../dlg-edit-album/dlg-edit-album.component';

@Component({
  selector: 'app-list-albums',
  standalone: true,
  imports: [
    BtnBackComponent,
    BtnActionComponent,
    BtnFabComponent,
    MomentPipe,
    RouterLink,
    AsyncPipe,
    BtnMoreComponent,
    LoadingCardComponent,
  ],
  templateUrl: './list-albums.component.html',
  styleUrl: './list-albums.component.css',
})
export class ListAlbumsComponent implements OnInit, OnDestroy {
  route: ActivatedRoute = inject(ActivatedRoute);
  roles: SchoolRolesService = inject(SchoolRolesService);
  private store: Store<AppState> = inject(Store<AppState>);
  toastr: ToastrService = inject(ToastrService);
  logger: NGXLogger = inject(NGXLogger);
  utilsService: UtilsService = inject(UtilsService);
  private dialog: MatDialog = inject(MatDialog);
  authService: AuthService = inject(AuthService);
  albumService: AlbumService = inject(AlbumService);
  private dialogService: DialogService = inject(DialogService);
  private router: Router = inject(Router);

  user: UserModel | null = null;

  albums$: Observable<Album[] | null> = new Observable();
  group: Group | null | undefined;

  loadingAlbums$: Observable<boolean> = of(true);

  groupId: string | null = this.route.snapshot.paramMap.get('id');

  membershipStatus = MembershipStatus;
  memberType = SchoolMemberType;
  rolesType = SchoolRoles;

  myRoles$: Observable<any> = new Observable();
  myRoles: any;

  exit$ = new Subject<void>();

  btnFabPosition = BtnFabPosition.LEFT;

  constructor() {
    this.route.paramMap.pipe(takeUntil(this.exit$)).subscribe((params) => {
      this.groupId = params.get('id');
    });

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );
    this.myRoles$.pipe(takeUntil(this.exit$)).subscribe((roles) => {
      this.myRoles = roles;
    });

    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.store
      .select(groupSelector.getGroupById(this.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
      });

    this.albums$ = this.store
      .select(albumSelector.getAlbumsByGroupId(this.groupId!))
      .pipe(takeUntil(this.exit$));

    this.loadingAlbums$ = this.store
      .select(albumSelector.loading)
      .pipe(takeUntil(this.exit$));
  }

  canManageAlbums(): boolean {
    if (this.group?.groupType === 'church') {
      return this.myRoles?.admin === true;
    }
    return this.myRoles?.management === true;
  }

  ngOnInit(): void {
    this.albums$.pipe(take(1)).subscribe((albums) => {
      if (albums && albums.length == 0) {
        this.getAlbums();
      }
    });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  async addMoreAlbums() {
    this.albums$.pipe(take(1)).subscribe(async (albums) => {
      if (albums && albums?.length > 0) {
        await this.getAlbums(albums![albums!.length - 1].albumDate);
      }
    });
    await this.getAlbums();
  }

  async getAlbums(lastAlbumDate?: Timestamp) {
    if (!this.user?.uid || !this.groupId) return;
    this.logger.debug('getAlbums for group: ', this.groupId);
    this.store.dispatch(
      albumActions.getAlbums({
        groupId: this.groupId,
        lastAlbumDate: lastAlbumDate,
      }),
    );
  }

  async onNewAlbum() {
    if (!this.groupId) return;

    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Create Album',
      description: `Create a new album`,
      action: 'Create',
      groupId: this.groupId,
    };
    const dialogRef = this.dialog.open(DlgCreateAlbumComponent, dialogConfig);
  }

  async onAddImages(item: Album) {
    this.logger.debug('Add images to album', item.id);

    // Fetch the group metadata from the store
    const metadata = await firstValueFrom(
      this.store.select(groupSelector.getSelectedGroupMetadata)
    );

    const currentUsageBytes = metadata?.storageUsageBytes || 0;
    const limitGb = metadata?.storageLimitGb || 5;
    const limitBytes = limitGb * 1024 * 1024 * 1024;

    if (currentUsageBytes >= limitBytes) {
      const buyNow = await this.dialogService.confirm({
        title: 'Storage Limit Reached',
        description: `Your subscribed storage of ${limitGb} GB has been reached. Please purchase additional storage to add more images.`,
        action: 'Buy Now',
        cancelText: 'Cancel',
      });

      if (buyNow && this.groupId) {
        this.router.navigate([
          this.group?.groupType === 'school' ? '/school' : '/groups',
          this.groupId,
          'billing',
          'addonsubscribe'
        ]);
      }
      return;
    }

    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      albumId: item.id,
      groupId: item.groupId,
    };
    const dialogRef = this.dialog.open(DlgAddImagesComponent, dialogConfig);
  }

  async onEditAlbum(item: Album) {
    if (!this.groupId) return;

    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Update Album',
      description: `Update album name`,
      action: 'Update',
      groupId: this.groupId,
      album: item,
    };
    const dialogRef = this.dialog.open(DlgEditAlbumComponent, dialogConfig);
  }

  // onDeleteAlbum(item: Album) {
  //   let dialogConfig = new MatDialogConfig();

  //   dialogConfig.data = {
  //     title: 'Delete Album',
  //     description: `Are you sure you want to delete album '${item.albumName}'?`,
  //     action: 'Delete',
  //     buttonColor: 'bg-red-500',
  //     showType: AlertType.error,
  //   };

  //   const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);

  //   return dialogRef.afterClosed().subscribe((result) => {
  //     if (result && item.groupId) {
  //       return this.store.dispatch(
  //         albumActions.deleteAction({ groupId: item.groupId, id: item.id! })
  //       );
  //     }
  //     return;
  //   });
  // }
}
